/* eslint-disable */
// Production Event API service for real-time subscriptions
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Cost-optimized production configuration
const EVENT_CONFIG = {
  ENABLE_PRESENCE: false, // DISABLED - saves $100+/month
  MAX_SUBSCRIPTIONS: 5, // Reduced from 10 - saves connection costs
  RECONNECT_ATTEMPTS: 3, // Reduced from 5 - saves failed connection costs
  RECONNECT_DELAY: 5000, // Increased from 2000 - reduces retry frequency
  HEARTBEAT_INTERVAL: 60000, // Increased from 30000 - reduces API calls by 50%
  CONNECTION_TIMEOUT: 30000, // Increased from 15000 - reduces timeout retries
  MESSAGE_RATE_LIMIT: 50, // Reduced from 100 - saves API costs
  NOTIFICATION_RATE_LIMIT: 25, // Reduced from 50 - saves API costs
  IDLE_DISCONNECT_TIME: 5 * 60 * 1000, // 5 minutes - disconnect idle connections
  BATCH_NOTIFICATIONS: true, // Enable notification batching
  BATCH_SIZE: 5, // Batch up to 5 notifications
  BATCH_DELAY: 2000 // 2 second delay for batching
};

class EventService {
  constructor() {
    this.subscriptions = new Map();
    this.connectionStatus = new Map();
    this.rateLimits = new Map();
    this.heartbeatIntervals = new Map();
    this.reconnectAttempts = new Map();
    this.lastActivity = new Map(); // Track user activity for idle disconnect
    this.notificationBatch = new Map(); // Batch notifications to reduce API calls
    this.batchTimers = new Map(); // Timers for batched notifications
  }

  // Cost optimization: Idle connection management
  updateActivity(subscriptionKey) {
    this.lastActivity.set(subscriptionKey, Date.now());
  }

  // Disconnect idle connections to save costs
  checkIdleConnections() {
    const now = Date.now();
    this.lastActivity.forEach((lastActive, subscriptionKey) => {
      if (now - lastActive > EVENT_CONFIG.IDLE_DISCONNECT_TIME) {

        this.unsubscribe(subscriptionKey);
      }
    });
  }

  // Batch notifications to reduce API calls
  batchNotification(userID, type, message, metadata) {
    if (!EVENT_CONFIG.BATCH_NOTIFICATIONS) {
      return false; // Batching disabled
    }

    const batchKey = `batch_${userID}`;
    
    if (!this.notificationBatch.has(batchKey)) {
      this.notificationBatch.set(batchKey, []);
    }

    const batch = this.notificationBatch.get(batchKey);
    batch.push({ type, message, metadata, timestamp: Date.now() });

    // Send batch when it reaches max size
    if (batch.length >= EVENT_CONFIG.BATCH_SIZE) {
      this.sendBatchedNotifications(userID);
      return true;
    }

    // Set timer to send batch after delay
    if (!this.batchTimers.has(batchKey)) {
      const timer = setTimeout(() => {
        this.sendBatchedNotifications(userID);
      }, EVENT_CONFIG.BATCH_DELAY);
      this.batchTimers.set(batchKey, timer);
    }

    return true; // Notification batched
  }

  // Send batched notifications as single API call
  async sendBatchedNotifications(userID) {
    const batchKey = `batch_${userID}`;
    const batch = this.notificationBatch.get(batchKey);
    
    if (!batch || batch.length === 0) return;

    // Clear batch and timer
    this.notificationBatch.delete(batchKey);
    const timer = this.batchTimers.get(batchKey);
    if (timer) {
      clearTimeout(timer);
      this.batchTimers.delete(batchKey);
    }

    // Combine messages into single notification
    const combinedMessage = batch.length === 1 
      ? batch[0].message
      : `${batch.length} new notifications: ${batch.map(n => n.message.substring(0, 50)).join(', ')}`;

    try {
      await this.sendNotification(userID, 'INFO', combinedMessage, {
        type: 'batched',
        count: batch.length,
        notifications: batch
      });
    } catch (error) {
      console.error('Failed to send batched notifications:', error);
    }
  }

  // Rate limiting
  checkRateLimit(key, limit) {
    const now = Date.now();
    const windowStart = now - 60000; // 1 minute window
    
    if (!this.rateLimits.has(key)) {
      this.rateLimits.set(key, []);
    }
    
    const requests = this.rateLimits.get(key);
    const recentRequests = requests.filter(time => time > windowStart);
    
    if (recentRequests.length >= limit) {
      return false;
    }
    
    recentRequests.push(now);
    this.rateLimits.set(key, recentRequests);
    return true;
  }

  // Cost-optimized connection monitoring
  startHeartbeat(subscriptionKey) {
    // Only start heartbeat for critical subscriptions
    if (!subscriptionKey.includes('notifications')) {
      return; // Skip heartbeat for non-critical subscriptions
    }
    
    const interval = setInterval(() => {
      const status = this.connectionStatus.get(subscriptionKey);
      if (status === 'connected') {

      }
    }, EVENT_CONFIG.HEARTBEAT_INTERVAL);
    
    this.heartbeatIntervals.set(subscriptionKey, interval);
  }

  stopHeartbeat(subscriptionKey) {
    const interval = this.heartbeatIntervals.get(subscriptionKey);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(subscriptionKey);
    }
  }

  // Enhanced reconnection logic
  async reconnectSubscription(subscriptionKey, subscribeFunction) {
    const attempts = this.reconnectAttempts.get(subscriptionKey) || 0;
    
    if (attempts >= EVENT_CONFIG.RECONNECT_ATTEMPTS) {
      console.error(`Max reconnection attempts reached for ${subscriptionKey}`);
      this.connectionStatus.set(subscriptionKey, 'failed');
      return null;
    }

    this.reconnectAttempts.set(subscriptionKey, attempts + 1);
    this.connectionStatus.set(subscriptionKey, 'reconnecting');
    
    const delay = EVENT_CONFIG.RECONNECT_DELAY * Math.pow(2, attempts);
    
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const subscription = await subscribeFunction();
          this.reconnectAttempts.set(subscriptionKey, 0);
          this.connectionStatus.set(subscriptionKey, 'connected');
          resolve(subscription);
        } catch (error) {
          console.error(`Reconnection attempt ${attempts + 1} failed for ${subscriptionKey}:`, error);
          resolve(await this.reconnectSubscription(subscriptionKey, subscribeFunction));
        }
      }, delay);
    });
  }

  // Cost-optimized notification subscription
  subscribeToNotifications(userID, callback) {
    if (!userID || typeof callback !== 'function') {
      throw new Error('Invalid parameters for notification subscription');
    }

    const subscriptionKey = `notifications_${userID}`;
    
    if (this.subscriptions.has(subscriptionKey)) {

      // Update activity to prevent idle disconnect
      this.updateActivity(subscriptionKey);
      return this.subscriptions.get(subscriptionKey);
    }

    // More aggressive subscription limit for cost control
    if (this.subscriptions.size >= EVENT_CONFIG.MAX_SUBSCRIPTIONS) {
      // Disconnect oldest idle connection to make room
      this.checkIdleConnections();
      if (this.subscriptions.size >= EVENT_CONFIG.MAX_SUBSCRIPTIONS) {
        throw new Error('Maximum subscription limit reached');
      }
    }


    this.connectionStatus.set(subscriptionKey, 'connecting');
    
    const subscribeFunction = () => {
      return getClient().graphql({
        query: `
          subscription OnNotificationCreated($userID: ID!) {
            onNotificationCreated(userID: $userID) {
              id
              userID
              type
              message
              isRead
              metadata
              createdAt
            }
          }
        `,
        variables: { userID }
      }).subscribe({
        next: (data) => {
          if (data?.data?.onNotificationCreated) {
            // Update activity timestamp
            this.updateActivity(subscriptionKey);
            // Reduced logging for cost savings

            this.connectionStatus.set(subscriptionKey, 'connected');
            callback(data.data.onNotificationCreated);
          }
        },
        error: (error) => {
          console.error('❌ Notification subscription error:', error);
          this.connectionStatus.set(subscriptionKey, 'error');
          
          // Auto-reconnect on error
          setTimeout(() => {
            this.reconnectSubscription(subscriptionKey, subscribeFunction);
          }, EVENT_CONFIG.RECONNECT_DELAY);
        }
      });
    };

    try {
      const subscription = subscribeFunction();
      this.subscriptions.set(subscriptionKey, subscription);
      this.startHeartbeat(subscriptionKey);
      this.connectionStatus.set(subscriptionKey, 'connected');
      return subscription;
    } catch (error) {
      console.error('Failed to create notification subscription:', error);
      this.connectionStatus.set(subscriptionKey, 'failed');
      throw error;
    }
  }

  // Subscribe to real-time chat messages
  subscribeToChat(conversationID, callback) {
    const subscriptionKey = `chat_${conversationID}`;
    
    if (this.subscriptions.has(subscriptionKey)) {

      return this.subscriptions.get(subscriptionKey);
    }


    
    const subscription = getClient().graphql({
      query: `
        subscription OnMessageCreated($conversationID: ID!) {
          onMessageCreated(conversationID: $conversationID) {
            id
            conversationID
            senderID
            receiverID
            content
            messageType
            isRead
            createdAt
            sender {
              id
              username
              displayName
              avatar
            }
          }
        }
      `,
      variables: { conversationID }
    }).subscribe({
      next: (data) => {

        callback(data.data.onMessageCreated);
      },
      error: (error) => {
        console.error('❌ Chat subscription error:', error);
      }
    });

    this.subscriptions.set(subscriptionKey, subscription);
    return subscription;
  }

  // Alias method for ChatContext compatibility
  subscribe(conversationID, callback) {
    return this.subscribeToChat(conversationID, callback);
  }

  // Cost-optimized notification sending with batching
  async sendNotification(userID, type, message, metadata = null) {
    // Input validation
    if (!userID || !type || !message) {
      throw new Error('Missing required notification parameters');
    }

    if (typeof message !== 'string' || message.length > 500) {
      throw new Error('Invalid message format or length');
    }

    // Try to batch notification first (saves API calls)
    if (this.batchNotification(userID, type, message, metadata)) {

      return { id: 'batched', status: 'batched' };
    }

    // Rate limiting (more aggressive for cost savings)
    const rateLimitKey = `notification_${userID}`;
    if (!this.checkRateLimit(rateLimitKey, EVENT_CONFIG.NOTIFICATION_RATE_LIMIT)) {
      // Instead of throwing error, batch the notification
      if (this.batchNotification(userID, type, message, metadata)) {
        return { id: 'rate_limited_batched', status: 'batched' };
      }
      throw new Error('Notification rate limit exceeded');
    }

    // Sanitize message for security
    const sanitizedMessage = message.replace(/<script[^>]*>.*?<\/script>/gi, '').trim();
    
    try {

      
      const result = await getClient().graphql({
        query: `
          mutation CreateNotificationWithEvent($input: CreateNotificationInput!) {
            createNotificationWithEvent(input: $input) {
              id
              userID
              type
              message
              isRead
              metadata
              createdAt
            }
          }
        `,
        variables: {
          input: {
            userID,
            type: type.toUpperCase(),
            message: sanitizedMessage,
            isRead: false,
            metadata: metadata ? JSON.stringify(metadata) : null
          }
        }
      });

      if (!result?.data?.createNotificationWithEvent) {
        throw new Error('Invalid response from notification service');
      }


      return result.data.createNotificationWithEvent;
    } catch (error) {
      console.error('❌ Error sending notification:', error);
      throw new Error(`Failed to send notification: ${error.message}`);
    }
  }

  // Send chat message with real-time trigger
  async sendMessage(conversationID, senderID, receiverID, content, messageType = 'text') {
    // Validate required parameters
    if (!conversationID || !senderID || !receiverID || !content) {
      throw new Error('Missing required parameters for sendMessage');
    }

    try {

      
      const result = await getClient().graphql({
        query: `
          mutation SendMessageCustom($input: CreateMessageInput!) {
            sendMessageCustom(input: $input) {
              id
              conversationID
              senderID
              receiverID
              content
              messageType
              isRead
              createdAt
              sender {
                id
                username
                displayName
                avatar
              }
            }
          }
        `,
        variables: {
          input: {
            conversationID,
            senderID,
            receiverID,
            content,
            messageType
          }
        }
      });

      if (!result?.data?.sendMessageCustom) {
        throw new Error('Invalid response from message service - no data returned');
      }


      return result.data.sendMessageCustom;
    } catch (error) {
      console.error('❌ Error sending message:', JSON.stringify(error, null, 2));
      throw new Error(`Failed to send message: ${error.message}`);
    }
  }

  // Production unsubscribe with cleanup
  unsubscribe(subscriptionKey) {
    if (this.subscriptions.has(subscriptionKey)) {

      
      try {
        const subscription = this.subscriptions.get(subscriptionKey);
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      } catch (error) {
        console.error('Error during unsubscribe:', error);
      }
      
      // Cleanup
      this.subscriptions.delete(subscriptionKey);
      this.connectionStatus.delete(subscriptionKey);
      this.reconnectAttempts.delete(subscriptionKey);
      this.stopHeartbeat(subscriptionKey);
    }
  }

  // Cost-optimized cleanup with batch notification cleanup
  unsubscribeAll() {

    
    // Send any pending batched notifications before cleanup
    this.notificationBatch.forEach((batch, batchKey) => {
      const userID = batchKey.replace('batch_', '');
      this.sendBatchedNotifications(userID).catch(console.error);
    });
    
    this.subscriptions.forEach((subscription, key) => {
      try {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      } catch (error) {
        console.error(`Error unsubscribing from ${key}:`, error);
      }
    });
    
    // Comprehensive cleanup including cost optimization data
    this.subscriptions.clear();
    this.connectionStatus.clear();
    this.reconnectAttempts.clear();
    this.rateLimits.clear();
    this.lastActivity.clear();
    this.notificationBatch.clear();
    
    // Clear all timers
    this.heartbeatIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.heartbeatIntervals.clear();
    
    this.batchTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.batchTimers.clear();
  }

  // Production presence subscription (disabled for cost optimization)
  subscribeToPresence(callback) {
    if (!EVENT_CONFIG.ENABLE_PRESENCE) {

      return { 
        unsubscribe: () => console.log('Mock presence subscription unsubscribed'),
        status: 'disabled'
      };
    }

    // Production presence implementation (when enabled)
    const subscriptionKey = 'presence_updates';
    
    if (this.subscriptions.has(subscriptionKey)) {

      return this.subscriptions.get(subscriptionKey);
    }


    this.connectionStatus.set(subscriptionKey, 'connecting');
    
    try {
      const subscription = getClient().graphql({
        query: `
          subscription OnPresenceUpdate {
            onPresenceUpdate {
              userID
              status
              lastSeen
            }
          }
        `
      }).subscribe({
        next: (data) => {
          if (data?.data?.onPresenceUpdate) {

            this.connectionStatus.set(subscriptionKey, 'connected');
            callback(data.data.onPresenceUpdate);
          }
        },
        error: (error) => {
          console.error('❌ Presence subscription error:', error);
          this.connectionStatus.set(subscriptionKey, 'error');
        }
      });

      this.subscriptions.set(subscriptionKey, subscription);
      this.startHeartbeat(subscriptionKey);
      return subscription;
    } catch (error) {
      console.error('Failed to create presence subscription:', error);
      this.connectionStatus.set(subscriptionKey, 'failed');
      throw error;
    }
  }

  // Production presence update (disabled for cost optimization)
  async updatePresence(userID, status, lastSeen = null) {
    if (!EVENT_CONFIG.ENABLE_PRESENCE) {

      return { userID, status: 'disabled', message: 'Presence tracking disabled' };
    }

    // Input validation
    if (!userID || !status) {
      throw new Error('Missing required presence parameters');
    }

    const validStatuses = ['online', 'away', 'offline', 'busy'];
    if (!validStatuses.includes(status.toLowerCase())) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }

    try {

      
      const result = await getClient().graphql({
        query: `
          mutation UpdatePresenceWithEvent($input: UpdatePresenceInput!) {
            updatePresenceWithEvent(input: $input) {
              userID
              status
              lastSeen
            }
          }
        `,
        variables: {
          input: {
            userID,
            status: status.toLowerCase(),
            lastSeen: lastSeen || new Date().toISOString()
          }
        }
      });

      if (!result?.data?.updatePresenceWithEvent) {
        throw new Error('Invalid response from presence service');
      }


      return result.data.updatePresenceWithEvent;
    } catch (error) {
      console.error('❌ Error updating presence:', error);
      return { userID, status: 'error', error: error.message };
    }
  }

  // Production service status and diagnostics
  getActiveSubscriptionCount() {
    return this.subscriptions.size;
  }

  getConnectionStatus(subscriptionKey) {
    return this.connectionStatus.get(subscriptionKey) || 'unknown';
  }

  getAllConnectionStatuses() {
    const statuses = {};
    this.connectionStatus.forEach((status, key) => {
      statuses[key] = status;
    });
    return statuses;
  }

  getServiceHealth() {
    const totalSubscriptions = this.subscriptions.size;
    const connectedCount = Array.from(this.connectionStatus.values())
      .filter(status => status === 'connected').length;
    
    return {
      totalSubscriptions,
      connectedSubscriptions: connectedCount,
      healthPercentage: totalSubscriptions > 0 ? (connectedCount / totalSubscriptions) * 100 : 100,
      status: connectedCount === totalSubscriptions ? 'healthy' : 'degraded',
      rateLimitStatus: this.rateLimits.size > 0 ? 'active' : 'inactive',
      presenceEnabled: EVENT_CONFIG.ENABLE_PRESENCE
    };
  }

  // Cleanup method for production
  destroy() {

    this.unsubscribeAll();
  }
}

// Create singleton instance with cost optimization
let eventService;

try {
  eventService = new EventService();
  
  // Cost optimization: Periodic idle connection cleanup
  if (typeof window !== 'undefined') {
    // Check for idle connections every 2 minutes
    setInterval(() => {
      eventService.checkIdleConnections();
    }, 2 * 60 * 1000);
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      eventService.destroy();
    });
    
    // Cleanup on page visibility change (user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // User switched away - start idle timer
        setTimeout(() => {
          if (document.hidden) {
            eventService.checkIdleConnections();
          }
        }, 30000); // 30 seconds after tab becomes hidden
      }
    });
  }
} catch (error) {
  console.error('Failed to initialize EventService:', error);
  // Fallback service for graceful degradation
  eventService = {
    subscribeToNotifications: () => ({ unsubscribe: () => {}, status: 'failed' }),
    subscribeToChat: () => ({ unsubscribe: () => {}, status: 'failed' }),
    subscribeToPresence: () => ({ unsubscribe: () => {}, status: 'failed' }),
    sendNotification: () => Promise.reject(new Error('EventService unavailable')),
    sendMessage: () => Promise.reject(new Error('EventService unavailable')),
    updatePresence: () => Promise.resolve({ status: 'unavailable' }),
    unsubscribe: () => {},
    unsubscribeAll: () => {},
    getActiveSubscriptionCount: () => 0,
    getServiceHealth: () => ({ status: 'failed', error: 'Service initialization failed' }),
    destroy: () => {}
  };
}

export default eventService;